import { useEffect, useState } from 'react';
import { useDateTime } from '../hooks/useDateTime';
import { DateInput } from '../components/shared/DateInput';
import { localIsoDate } from '../utils/local-date';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Select, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '../i18n';
import { AttendanceReportPanel } from '../components/attendance/AttendanceReportPanel';
import { AttendancePhotoGrid } from '../components/AttendancePhotoGrid';
import { fetchSessionPhotos, fetchAttendanceSession, fetchAttendanceSessions, fetchAttendanceStampedPhoto } from '../api/attendance.api';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, padding: tokens.spacingVerticalM, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  muted: { color: tokens.colorNeutralForeground3 },
  cols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: tokens.spacingHorizontalM },
});

const today = () => localIsoDate();

/** Listas de asistencia por día y cuadrilla, con presentes, faltantes y la foto de cada registro. */
export function AttendancePage() {
  const t = useT();
  const a = t.attendancePage;
  const s = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [date, setDate] = useState(today());
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<'day' | 'report'>('day');

  const sessions = useQuery({ queryKey: ['att-sessions', tenantId, date], queryFn: () => fetchAttendanceSessions(tenantId!, date), enabled: !!tenantId, retry: false });
  const detail = useQuery({ queryKey: ['att-session', tenantId, openId], queryFn: () => fetchAttendanceSession(tenantId!, openId!), enabled: !!tenantId && !!openId });

  const photos = useQuery({ queryKey: ['att-session-photos', tenantId, openId], queryFn: () => fetchSessionPhotos(tenantId!, openId!), enabled: !!tenantId && !!openId });

  // Las miniaturas son la foto sellada con el contorno de todas las personas que salen en ella.
  const [stampedThumbs, setStampedThumbs] = useState<Record<string, string>>({});
  useEffect(() => {
    const list = photos.data;
    if (!list || !openId || !tenantId) return;
    let alive = true;
    const urls: string[] = [];
    void Promise.all(list.map(async (p) => {
      try {
        const url = URL.createObjectURL(await fetchAttendanceStampedPhoto(tenantId, openId, p.recordId, true));
        urls.push(url);
        if (alive) setStampedThumbs((m) => ({ ...m, [p.recordId]: url }));
      } catch { /* se queda la foto limpia */ }
    }));
    return () => { alive = false; setStampedThumbs({}); urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [photos.data, openId, tenantId]);

  const disabled = (sessions.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error === 'FEATURE_DISABLED';
  if (disabled) return <div className={s.page}><Text>{a.disabled}</Text></div>;

  // El sello (hora del servidor, ubicación, lugar) se arma al pedirlo; la foto original no se modifica.
  const openStamped = async (sessionId: string, recordId: string) => {
    const url = URL.createObjectURL(await fetchAttendanceStampedPhoto(tenantId!, sessionId, recordId));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const fmt = useDateTime();
  const name = (id: string) => detail.data?.employees[id]?.name ?? id;

  return (
    <div className={s.page}>
      <div>
        <Text as="h1" size={600} weight="semibold" block>{a.attendanceTitle}</Text>
        <Text className={s.muted} block>{a.subtitleAttendance}</Text>
      </div>
      <div className={s.row} role="tablist">
        <Button role="tab" aria-selected={view === 'day'} appearance={view === 'day' ? 'primary' : 'secondary'} onClick={() => setView('day')}>{a.tabDay}</Button>
        <Button role="tab" aria-selected={view === 'report'} appearance={view === 'report' ? 'primary' : 'secondary'} onClick={() => setView('report')}>{a.tabReport}</Button>
      </div>
      {view === 'report' && tenantId && <AttendanceReportPanel tenantId={tenantId} />}
      {view === 'day' && (
      <>
      <div className={s.row}>
        <DateInput aria-label={a.date} value={date} onValueChange={(iso) => { if (iso) { setDate(iso); setOpenId(null); } }} />
      </div>

      {sessions.isLoading ? <Spinner size="small" label={a.loading} /> : (sessions.data?.length ?? 0) === 0 ? <Text className={s.muted}>{a.noSessions}</Text> : (
        <div className={s.row}>
          <Select aria-label={a.listLabel} value={openId ?? ''} onChange={(_, d) => setOpenId(d.value || null)} style={{ minWidth: 280 }}>
            <option value="">{a.pickList(sessions.data!.length)}</option>
            {sessions.data!.map((x) => (
              <option key={x.sessionId} value={x.sessionId}>
                {(x.crewNames?.length ? x.crewNames.join(' · ') : a.allStaff)} · {x.status === 'OPEN' ? a.open : a.closed}{x.openedAt ? ` · ${fmt.time(x.openedAt)}` : ''}
              </option>
            ))}
          </Select>
        </div>
      )}

      {openId && detail.isLoading && <Spinner size="small" label={a.loading} />}
      {detail.data && (
        <div className={s.cols}>
          <div className={s.card}>
            <Text weight="semibold">{a.present} ({detail.data.roster.presentEmployeeIds.length + detail.data.roster.extras.length})</Text>
            {detail.data.roster.presentEmployeeIds.map((id) => {
              const rec = detail.data!.records.find((r) => r.employeeId === id);
              return (
                <div key={id} className={s.row}>
                  <Text>{name(id)}</Text>
                  {rec && <Badge appearance="tint" size="small">{a.methods[rec.method as keyof typeof a.methods] ?? rec.method}</Badge>}
                  {rec?.hasPhoto && (
                    <>
                      <Button size="small" appearance="subtle" onClick={() => openStamped(detail.data!.session.sessionId, rec.recordId)}>{a.viewStamped}</Button>
                    </>
                  )}
                </div>
              );
            })}
            {detail.data.roster.extras.map((x) => <Text key={x.recordId}>{x.name} <Badge appearance="tint" size="small">{a.extras}</Badge></Text>)}
          </div>
          <div className={s.card}>
            <Text weight="semibold">{a.missing} ({detail.data.roster.missingEmployeeIds.length})</Text>
            {detail.data.roster.missingEmployeeIds.map((id) => {
              const d = detail.data!.session.missingDecisions[id];
              return (
                <div key={id} className={s.row}>
                  <Text>{name(id)}</Text>
                  <Badge appearance="tint" size="small" color={d ? (d.decision === 'ABSENT' ? 'danger' : 'warning') : 'informative'}>
                    {d ? (d.decision === 'ABSENT' ? a.absent : a.justified) : a.undecided}
                  </Badge>
                  {d?.reason && <Text className={s.muted}>{d.reason}</Text>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {openId && detail.data && (detail.data.session.loteId || detail.data.session.notes) && (
        <div className={s.card}>
          {detail.data.session.loteId && (
            <Text>{a.destination}: {sessions.data?.find((x) => x.sessionId === openId)?.loteName ?? detail.data.session.loteId}</Text>
          )}
          {detail.data.session.notes && <Text>{a.sessionNotes}: {detail.data.session.notes}</Text>}
        </div>
      )}
      {openId && detail.data && (
        <div className={s.card}>
          <Text weight="semibold">{a.sessionPhotos}</Text>
          <AttendancePhotoGrid
            loading={photos.isLoading}
            empty={a.noSessionPhotos}
            photos={photos.data?.map((p) => ({
              url: stampedThumbs[p.recordId] ?? p.url, // mientras carga el sello, la foto limpia
              caption: p.names.join(', '),
              onOpen: () => openStamped(detail.data!.session.sessionId, p.recordId),
            }))}
          />
        </div>
      )}
      </>
      )}
    </div>
  );
}
